import SecondaryButton from '@components/V3/Utils/SecondaryButton';
import { MentorDetails } from '@shared/common';
import { MENTOR_STATUS_V3 } from '@shared/constants';
import AgoraBigSelector from '@utils/AgoraBigSelector';
import AgoraInput from '@utils/AgoraInput';
import React, { useState } from 'react';
import { COLORS } from '../../../v3/global';
import './create-new-user.styles.scss';
import { CreateMentorDetails } from '.';
import { useGetAllCourses } from '@actions';
import Select from '@components/V4/Select/Select';
import Input from '@components/V4/Inputs/Input';
import { countries } from 'country-codes-flags-phone-codes';

type Props = {
  handleCurrentPage: (page: string) => void;
  mentorDetails: CreateMentorDetails;
  handleInputChangeMentor: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleArrayChangeMentor: (name: keyof CreateMentorDetails, value: string) => void;
  handleArrayRemoveMentor: (name: keyof CreateMentorDetails, index: number) => void;
  handleCoursesCanTeachSelect: (courses: any[]) => void;
  handleStatusMentor: (status: any) => void;
  setMentorDetails: React.Dispatch<React.SetStateAction<CreateMentorDetails>>;
};

const CreateNewUserMentorStepOne = ({
  handleCurrentPage,
  mentorDetails,
  handleInputChangeMentor,
  handleArrayChangeMentor,
  handleArrayRemoveMentor,
  handleCoursesCanTeachSelect,
  handleStatusMentor,
  setMentorDetails,
}: Props) => {
  const { data: allCourses } = useGetAllCourses();

  const [touched, setTouched] = useState<Record<keyof MentorDetails, boolean>>({
    firstName: false,
    lastName: false,
    phoneNumber: false,
    coursesCanTeach: false,
    mentorStatus: false,
    payRate: false,
    currency: false,
    billingCurrency: false,
    vatPayer: false,
    company: false,
    companyCountry: false,
    companyAddress: false,
    fiscalRegistrationNumber: false,
    companyIBAN: false,
  });

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const getColor = (name: keyof typeof mentorDetails) => {
    if (
      !touched[name] &&
      (name === 'coursesCanTeach' ? !mentorDetails?.[name]?.length : !mentorDetails?.[name])
    ) {
      return COLORS.TEXT_SECONDARY;
    } else {
      if (name === 'coursesCanTeach') {
        return mentorDetails?.[name]?.length ? COLORS.PRIMARY : COLORS.ERROR;
      } else {
        return mentorDetails[name] ? COLORS.PRIMARY : COLORS.ERROR;
      }
    }
  };

  const areDetailsFilled = !!(
    mentorDetails.firstName &&
    mentorDetails.lastName &&
    mentorDetails.phoneNumber &&
    mentorDetails.coursesCanTeach &&
    mentorDetails.coursesCanTeach.length >= 1 &&
    mentorDetails.mentorStatus
  );

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}></div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h3
            style={{
              color: COLORS.TEXT_SECONDARY,
              marginBottom: '24px',
            }}
            className="modal-page-section-title"
          >
            Basic Info/ Contact Info:
          </h3>

          <div className="">
            <AgoraInput
              htmlFor="firstName"
              type="text"
              name="firstName"
              id="firstName"
              placeholder=""
              label="First Name"
              color={getColor('firstName')}
              labelColor={getColor('firstName')}
              borderColor={getColor('firstName')}
              labelMargin="0 0 0 8px"
              value={mentorDetails?.firstName}
              margin="0px 0px 24px 0px"
              onChange={handleInputChangeMentor}
              onBlur={handleBlur}
              isRequired
            />

            <AgoraInput
              htmlFor="lastName"
              type="text"
              name="lastName"
              id="lastName"
              placeholder=""
              label="Last Name"
              color={getColor('lastName')}
              labelColor={getColor('lastName')}
              borderColor={getColor('lastName')}
              labelMargin="0 0 0 8px"
              value={mentorDetails?.lastName}
              margin="0px 0px 24px 0px"
              onChange={handleInputChangeMentor}
              onBlur={handleBlur}
              isRequired
            />
          </div>

          <div className="flex flex-row gap-2">
            <Select
              className="max-w-[15%]"
              value={mentorDetails.phoneNumber.countryCode}
              onSelect={(value) =>
                setMentorDetails((prevState) => ({
                  ...prevState,
                  phoneNumber: {
                    countryCode: String(value),
                    number: prevState.phoneNumber.number,
                  },
                }))
              }
              options={countries.map((country) => ({
                label: `(${country.dialCode}) ${country.name}`,
                value: country.dialCode,
              }))}
              label="Country Code"
              isRequired
              size="large"
            />

            <Input
              type="tel"
              id="phone"
              label="Phone Number"
              name="phoneNumber.number"
              prefix={mentorDetails.phoneNumber.countryCode}
              value={mentorDetails.phoneNumber.number}
              onChange={(e) =>
                setMentorDetails((prevState) => ({
                  ...prevState,
                  phoneNumber: {
                    countryCode: prevState.phoneNumber.countryCode,
                    number: String(e.target.value),
                  },
                }))
              }
              isRequired
            />
          </div>

          <h3 className="modal-page-section-title text-customGrey mb-6 mt-8">
            Mentoring Information:
          </h3>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ width: 'calc(70% - 12px)' }}>
              <AgoraBigSelector
                label="Courses can teach"
                labelColor={getColor('coursesCanTeach')}
                labelMargin="0 0 8px 0"
                onSelect={(value) => {
                  handleCoursesCanTeachSelect([...mentorDetails.coursesCanTeach, value]);
                  setTouched((prev) => ({
                    ...prev,
                    coursesCanTeach: true,
                  }));
                }}
                onDeselect={(value) => {
                  const updatedCourses = mentorDetails?.coursesCanTeach?.filter(
                    (course) => course._id !== value
                  );
                  handleCoursesCanTeachSelect(updatedCourses);
                }}
                options={allCourses?.map((course) => ({
                  label: course?.title,
                  value: course?._id,
                }))}
                onClear={() => {
                  handleCoursesCanTeachSelect([]);
                }}
                labelKey="label"
                valueKey="value"
                margin="0 0 8px 0"
                minHeight="38px"
                width="100%"
                borderRadius="8px"
                borderColor={getColor('coursesCanTeach')}
                mode="multiple"
                value={mentorDetails.coursesCanTeach}
                isRequired
              />
            </div>

            <div
              style={{
                width: 'calc(30% - 12px)',
                marginTop: '-9px',
              }}
            >
              <AgoraBigSelector
                label="Mentor Status"
                labelColor={getColor('mentorStatus')}
                onSelect={(value) => {
                  handleStatusMentor(value);
                  setTouched((prev) => ({
                    ...prev,
                    mentorStatus: true,
                  }));
                }}
                options={MENTOR_STATUS_V3}
                showOptionsBy="value"
                margin="0 0 8px 0"
                height="38px"
                width="100%"
                borderRadius="8px"
                borderColor={getColor('mentorStatus')}
                isRequired
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col mt-6">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '24px',
            }}
          >
            <span style={{ marginRight: '16px' }} className="create-new-user-modal-steps-text">
              Step 1 of 2
            </span>
            <div
              style={{
                width: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div className="create-new-user-modal-step-container">
                <div className="create-new-user-modal-step-circle"></div>
              </div>

              <div
                style={{
                  backgroundColor: COLORS.TEXT_SECONDARY,
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                }}
              ></div>
            </div>
          </div>
          <SecondaryButton
            onClick={() => {
              if (areDetailsFilled) handleCurrentPage('create-new-user-mentor-step-two');
            }}
            backgroundColor={COLORS.WHITE}
            height="40px"
            padding="12px"
            margin="0 0 0 0"
            width="100%"
            disabled={!areDetailsFilled}
          >
            <span className="create-new-user-modal-submit-button" style={{ color: COLORS.BLACK }}>
              Continue
            </span>
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
};

export default CreateNewUserMentorStepOne;
